import { useState, useEffect } from "react";
import { FilterMatchMode } from "primereact/api";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import "./MedicalCasesListing.css";
import { Dropdown } from "primereact/dropdown";
import { currencyFormat, formatCi, formatFullNumber } from "../utils";
import { Link } from "react-router-dom";
import StatusTag from "../components/StatusTag";
import { CaseStatus, UserRole } from "../constants";
import Dashboard from "../Layout/Dashboard";
import {
  useRequirementTypes,
  useClients,
  useGetInsurance,
  useStaleMedicalCases,
  useMedicalCasesCount
} from "../hooks/queries";
import { Calendar } from "primereact/calendar";
import { FloatLabel } from "primereact/floatlabel";
import { Paginator } from "primereact/paginator";
import { PaginatorTemplate } from "../Layout/Paginator/PaginatorTemplate";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import ExportCasesDialog from "./Modals/ExportCasesDialog";
import { Card } from "primereact/card";

/**
 * Template to show the amount in currency format
 */
const amountBodyTemplate = (rowData) => {
  return currencyFormat(rowData.amount ?? 0);
};

/**
 * Status tag for filter selector
 * @param {string} option
 */
const statusItemTemplate = (option) => {
  return <StatusTag value={option?.value} />;
};

const caseStatusArray = [
  {
    label: CaseStatus.labels[CaseStatus.PENDIENTE],
    value: CaseStatus.PENDIENTE,
  },
  {
    label: CaseStatus.labels[CaseStatus.RESCHEDULED],
    value: CaseStatus.RESCHEDULED,
  },
];

/**
 * Status filter dropdown
 * @param {string[]} options
 */
const statusRowFilterTemplate = (options) => {
  return (
    <Dropdown
      value={options.value}
      options={caseStatusArray}
      onChange={(e) => {
        options.filterApplyCallback(e.value);
      }}
      itemTemplate={statusItemTemplate}
      placeholder="Seleccionar status"
      className="p-column-filter min-w-min"
      showClear
    />
  );
};

/**
 * Medical cases table
 */
export default function StaleCasesListing() {
  const role = localStorage.getItem("role");

  const storedDate = localStorage.getItem("hideModal", null);

  const [showDialog, setShowDialog] = useState(storedDate === null);
  const [showExportDialog, setShowExportDialog] = useState(false);

  const [pageSize, setPageSize] = useState(20);
  const [lazyFilters, setLazyFilters] = useState({
    status: null,
  });
  const [lazyState, setLazyState] = useState({
    first: 1,
    rows: 10,
    page: 1,
    sortField: null,
    sortOrder: null,
    filters: {},
  });
  const casesCountQuery = useMedicalCasesCount();
  
  useEffect(() => {
    if (storedDate) {
      const currentDate = new Date();
      if(storedDate === null) {
        setShowDialog(true);
        // localStorage.setItem("hideModalUntil",currentDate);
      }

      if (currentDate - storedDate >= 60* 1000) {
        setShowDialog(true);
        localStorage.setItem("hideModal",currentDate);
      } 
    }
  }, [storedDate]);

  // Get medical cases
  const staleCases = useStaleMedicalCases({
    page: lazyState.page,
    pageSize,
    filters: lazyFilters,
  });

  // Case detail link
  const ViewDetailBodyTemplate = (row) => (
    <Link to={`/casos/${row.id}`} className="p-button text-sm p-2">
      <span className="p-button-icon p-button-icon-left pi pi-eye"></span>
      <span className="p-button-label">Ver</span>
    </Link>
  );

  // Status change
  const [dateFilter, setDateFilter] = useState(null);

  // Table multiple selection
  const [selectedRows, setSelectedRows] = useState(null);

  // Get requirement types
  const requirementTypesQuery = useRequirementTypes();

  const clientsQuery = useClients(false);

  const useInsurance = useGetInsurance();
  const insurance = useInsurance?.data;

  // Global filters
  const [filters] = useState({
    company_client: { value: null, matchMode: FilterMatchMode.CONTAINS },
    created: { value: null, matchMode: FilterMatchMode.CONTAINS },
    id: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.CONTAINS },
    "patient_.sex": { value: null, matchMode: FilterMatchMode.EQUALS },
    requirement_type_names: { value: null, matchMode: FilterMatchMode.EQUALS },
    "holder_.ci": { value: null, matchMode: FilterMatchMode.CONTAINS },
    "patient_.ci": { value: null, matchMode: FilterMatchMode.CONTAINS },
    "patient_.name": { value: null, matchMode: FilterMatchMode.CONTAINS },
    requirement_names: { value: null, matchMode: FilterMatchMode.CONTAINS },
    "patient_.birthdate": { value: null, matchMode: FilterMatchMode.CONTAINS },
    diagnosis_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    medical_center_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    subsidiary_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    "patient_.phone": { value: null, matchMode: FilterMatchMode.CONTAINS },
    state_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const handleExportClick = () => {
    setShowExportDialog(true);
  };
  const handleExportDialogHide = () => {
    setShowExportDialog(false);
  };

  /**
   * Status tag for table body
   */
  const statusBodyTemplate = (rowData) => (
    <>
      <StatusTag value={rowData.status} />
      {rowData.prioritized &&
        (rowData.status === CaseStatus.PENDIENTE ||
          rowData.status === CaseStatus.RESCHEDULED) && (
          <StatusTag className="mt-2" value={CaseStatus.PRIORITIZED} />
        )}
    </>
  );

  const clientRowFilterTemplate = (options) => {
    return (
      <Dropdown
        value={options.value}
        options={clientsQuery.data ?? []}
        optionLabel="name"
        optionValue="id"
        onChange={(e) => {
          options.filterApplyCallback(e.value);
        }}
        placeholder="Seleccionar cliente"
        className="p-column-filter min-w-min"
        showClear
      />
    );
  };

  /**
   * Dropdown to filter case requirement type
   */
  const requirementTypeFilterTemplate = (options) => (
    <Dropdown
      value={options.value}
      loading={requirementTypesQuery.isLoading}
      disabled={requirementTypesQuery.isLoading}
      options={requirementTypesQuery.data ?? []}
      optionValue="id"
      optionLabel="name"
      onChange={(e) => {
        return options.filterApplyCallback(e.value);
      }}
      placeholder="Seleccionar tipo"
      className="p-column-filter min-w-min"
      showClear
      filter
      style={{ maxWidth: "10rem" }}
    />
  );

  /**
   * Page event
   */
  const onPageChange = (e) => {
    setPageSize(e.rows);
    setLazyState({ ...lazyState, first: e.first, page: e.page + 1 });
  };

  const onFilter = (event) => {
    event["first"] = 0;
    setLazyState(event);
    let status = event.filters.status.value;
    let prioritized = null;
    if (status === CaseStatus.PRIORITIZED) {
      status = null;
      prioritized = true;
    }

    setLazyFilters({
      ...lazyFilters,
      subsidiary__company_client: event.filters.company_client.value,
      company_client: event.filters.company_client.value,
      status: status,
      prioritized: prioritized,
      id__contains: event.filters.id.value,
      holder__ci__icontains: event.filters["holder_.ci"].value,
      patient__ci__icontains: event.filters["patient_.ci"].value,
      patient__name__icontains: event.filters["patient_.name"].value,
      medical_center__name__icontains: event.filters.medical_center_name.value,
      requirements__requirement__type_requirement:
        event.filters.requirement_type_names.value,
      state__name__icontains: event.filters.state_name.value,
    });
  };

  /**
   * Cases table header
   */
  const TableHeader = () => {
    return (
      <div className="full-w-header">
        <div className=" mb-2">
          <FloatLabel>
            <Calendar
              locale="es"
              inputId="created_date"
              value={dateFilter}
              onChange={(e) => {
                if (
                  e.originalEvent.type === "click" ||
                  e.originalEvent.type === "keypress"
                ) {
                  setDateFilter(e.value);

                  const dateFormat = e.value?.toISOString().split("T")[0];
                  setLazyFilters({
                    ...lazyFilters,
                    created_at__date: dateFormat,
                  });
                }
                if (e.value === dateFilter) return;

                if (!e.value) {
                  setDateFilter(null);
                }
              }}
              dateFormat="mm/dd/yy"
              placeholder="dia/mes/año"
              mask="99/99/9999"
              showButtonBar
            />
            <label htmlFor="created_date">Buscar por fecha</label>
          </FloatLabel>
        </div>
        <div>
          <Button
            label="Exportar"
            icon="pi pi-file-pdf"
            severity="warning"
            className="p-button-success"
            onClick={handleExportClick}
          />
        </div>
      </div>
    );
  };

  return (
    <Dashboard headerTitle="Casos pendientes">
      <div className="card">
        <Dialog
          draggable={false}
          visible={showDialog}
          onHide={() => setShowDialog(false)}
          header="Tienes casos pendientes"
          headerClassName="pb-2"
          className="md:w-7"
        >
          <div className="p-fluid">
            <p>
              Tienes{" "}
              {staleCases.isLoading ? (
                <i className="pi pi-spin pi-spinner "></i>
              ) : (
                <b className="text-2xl text-red-600">
                  {staleCases.data?.count}
                </b>
              )}{" "}
              casos pendientes por cambio de estatus. Para seguir recibiendo
              nuevos pacientes debes dar respuesta a estos casos:
            </p>
            <ul className="flex flex-column gap-2">
              <li>
                Tendrás hasta <strong>7</strong> días para cambiar los casos de{" "}
                <StatusTag value={CaseStatus.PENDIENTE} /> a{" "}
                <StatusTag value={CaseStatus.RESCHEDULED} />.
              </li>
              <li>
                Tendrás hasta <strong>35</strong> días para cambiar los casos de{" "}
                <StatusTag value={CaseStatus.RESCHEDULED} /> a{" "}
                <StatusTag value={CaseStatus.ATENDIDO} />.
              </li>
            </ul>
            <p>
              Si marcas un caso como <StatusTag value={CaseStatus.ANULADO} />,
              este podría ser asignado a otro Centro Médico.
            </p>
            <div className="flex justify-content-end gap-2">
              {/* <Button
                label="No mostrar más por hoy"
                className="w-fit"
                text
                severity="secondary"
                onClick={() => {
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  localStorage.setItem(
                    "hideModalUntil",
                    tomorrow.toISOString()
                  );
                  setShowDialog(false);
                }}
              /> */}
              <Button
                label="Continuar"
                className="w-fit px-6"
                onClick={() => setShowDialog(false)}
                disabled={staleCases.isLoading}
              />
            </div>
          </div>
        </Dialog>

        <div className="flex flex-wrap gap-3 mb-2">
          <Card title="" className="w-3 my-2 card-count md:w-3 lg:w-2">
            {casesCountQuery.data && (
              <div className="text-xl text-center">
                <p className="m-0">
                  <strong>{casesCountQuery.data?.prioritized_count}</strong>
                </p>
                {/* <Tag value="Priorizado" className="bg-green-500" /> */}
                <StatusTag value={CaseStatus.PRIORITIZED} />
              </div>
            )}
          </Card>
          <Card title="" className="w-3 mt-2 mb-2 card-count md:w-3  lg:w-2">
            {casesCountQuery.data && (
              <div className="text-xl text-center">
                <p className="m-0">
                  <strong>{casesCountQuery.data?.pending_count}</strong>
                </p>
                <StatusTag value={CaseStatus.PENDIENTE} />
              </div>
            )}
          </Card>
          <Card title="" className="w-3 mt-2 mb-2 card-count md:w-3 lg:w-2">
            {casesCountQuery.data && (
              <div className="text-xl text-center">
                <p className="m-0">
                  <strong>{casesCountQuery.data?.scheduled_count}</strong>
                </p>
                <StatusTag value={CaseStatus.RESCHEDULED} />
              </div>
            )}
          </Card>
          <Card title="" className="w-3 mt-2 mb-2 card-count md:w-3 lg:w-2">
            {casesCountQuery.data && (
              <div className=" text-center">
                <p className="text-xl m-0">
                  <strong>{casesCountQuery.data?.attended_count}</strong>
                </p>
                <StatusTag value={CaseStatus.ATENDIDO} />
              </div>
            )}
          </Card>
          <Card title="" className="w-3 mt-2 mb-2 card-count md:w-3 lg:w-2">
            {casesCountQuery.data && (
              <div className=" text-center">
                <p className="text-xl m-0">
                  <strong>{casesCountQuery.data?.remitted_count}</strong>
                </p>
                <StatusTag value={CaseStatus.REMESADO} />
              </div>
            )}
          </Card>
        </div>
        <DataTable
          value={staleCases.data?.results ?? []}
          dataKey="id"
          size={"small"}
          filters={filters}
          loading={staleCases.isLoading}
          onFilter={onFilter}
          header={<TableHeader />}
          emptyMessage="No se hallaron casos."
          selectionMode="checkbox"
          selection={selectedRows}
          onSelectionChange={(e) => setSelectedRows(e.value)}
          stripedRows
          scrollable
          removableSort
        >
          <Column
            field="company_client"
            header="Cliente"
            filter
            filterPlaceholder="Buscar por Cliente"
            filterElement={clientRowFilterTemplate}
            showFilterMatchModes={false}
            showClearButton={false}
            showApplyButton={false}
            body={(row) => {
              if (role === UserRole.MEDICAL_CENTER) {
                return row.parent_medical_center_name ?? row.company_client;
              }
              return row.company_client;
            }}
          />
          <Column
            field="created"
            header="Fecha"
            dataType="date"
            filterPlaceholder="Buscar por fecha"
          />
          <Column
            field="service_id"
            header="Nro. Siniestro"
            filter
            filterPlaceholder="Buscar Nro. de Siniestro"
            showFilterMatchModes={false}
            showClearButton={false}
            showApplyButton={false}
          />
          <Column
            field="id"
            header="ID #"
            sortable
            filter
            filterPlaceholder="Buscar por Nro. de Caso"
            showFilterMatchModes={false}
            showClearButton={false}
            showApplyButton={false}
          />
          <Column
            field="holder_.ci"
            header="CI Titular"
            filter
            filterPlaceholder="Buscar por Titular"
            showFilterMatchModes={false}
            showClearButton={false}
            showApplyButton={false}
            bodyClassName="readable-cell"
            body={(row) => formatCi(row.holder_.ci)}
          />
          <Column
            field="patient_.ci"
            header="CI Paciente"
            filter
            filterPlaceholder="Buscar por CI Paciente"
            showFilterMatchModes={false}
            showClearButton={false}
            showApplyButton={false}
            bodyClassName="readable-cell"
            body={(row) => formatCi(row.patient_.ci)}
          />
          <Column
            field="patient_.name"
            header="Nombre Paciente"
            filter
            filterPlaceholder="Buscar por Nombre"
            showFilterMatchModes={false}
            showClearButton={false}
            showApplyButton={false}
            bodyClassName="readable-cell"
          />
          <Column
            field="patient_.phone"
            header="Teléfono Paciente"
            filter
            filterPlaceholder="Buscar por Teléfono"
            showFilterMatchModes={false}
            showClearButton={false}
            showApplyButton={false}
            bodyClassName="readable-cell"
            body={(row) => formatFullNumber(row.patient_.phone)}
          />
          {insurance?.name === "Vértice Seguros" && (
            <Column
              field="state_name"
              header="Estado"
              filter
              filterPlaceholder="Buscar por Estado"
              showFilterMatchModes={false}
              showClearButton={false}
              showApplyButton={false}
              bodyClassName="readable-cell"
              sortable
            />
          )}
          <Column
            field="type_requirement_name"
            header="Tipo de requerimiento"
            filter
            filterElement={requirementTypeFilterTemplate}
            showFilterMatchModes={false}
            showClearButton={false}
            showApplyButton={false}
          />
          <Column
            field="requirements_name"
            header="Requerimiento"
            filter
            filterPlaceholder="Buscar por Requerimiento"
            showFilterMatchModes={false}
            showClearButton={false}
            showApplyButton={false}
            bodyClassName="readable-cell"
            body={(row) =>
              row.requirements_name.slice(0, 60) +
              (row.requirements_name.length > 60 ? "..." : "")
            }
          />
          <Column
            field="amount"
            header={
              insurance?.name === "Vértice Seguros"
                ? "Monto REF"
                : "Monto (Bs.)"
            }
            filter
            filterPlaceholder="Buscar por Monto"
            showFilterMenu={false}
            body={amountBodyTemplate}
            bodyClassName="readable-cell"
            sortable
          />
          <Column
            field="diagnosis_name"
            header="Diagnóstico"
            filter
            filterPlaceholder="Buscar por Diagnóstico"
            showFilterMatchModes={false}
            showClearButton={false}
            showApplyButton={false}
            bodyClassName="readable-cell"
          />
          <Column
            field="medical_center_name"
            header="Proveedor"
            filter
            filterPlaceholder="Buscar por Proveedor"
            showFilterMatchModes={false}
            showClearButton={false}
            showApplyButton={false}
            bodyClassName="readable-cell"
          />
          <Column
            field="status"
            header="Status"
            showFilterMatchModes={false}
            showClearButton={false}
            showApplyButton={false}
            body={statusBodyTemplate}
            filter
            filterElement={statusRowFilterTemplate}
            frozen
            alignFrozen="right"
          />
          <Column
            header="Acción"
            body={ViewDetailBodyTemplate}
            frozen
            alignFrozen="right"
          />
        </DataTable>
        <Paginator
          first={lazyState.first}
          rows={pageSize}
          totalRecords={staleCases.data?.count}
          template={PaginatorTemplate}
          onPageChange={onPageChange}
        />
        <ExportCasesDialog
          visible={showExportDialog}
          onHide={handleExportDialogHide}
        />
      </div>
    </Dashboard>
  );
}
